import type { ActionPerformed, Token } from '@capacitor/push-notifications';
import { clientPlatform, isNativePlatform } from './platform.js';
import { request } from './api.js';

/**
 * Native-only push (FCM) client (M4.3). On web every export is a no-op.
 *
 * Lifecycle:
 *   - `initPushNotifications()` — wire listeners once, request the Android-13+
 *     runtime permission, then `register()`. Re-callable on each login; the
 *     plugin re-emits `registration` so the token is re-POSTed after a logout
 *     deleted it server-side.
 *   - `removePushRegistration()` — DELETE the token on logout.
 *   - `setPushNavigator()` — hand in React Router's navigate so a notification
 *     tap can deep-link to the carried `route`.
 *
 * Plugin gotcha (carried from the secure-storage work): never RETURN a
 * Capacitor plugin proxy from an async function — every property access on it
 * is bridged natively and `.then`-probing hangs the caller. We only ever read
 * the named export from the module namespace and call methods on it.
 */
let listenersWired = false;
let registeredToken: string | null = null;
let navigateRef: ((route: string) => void) | null = null;

async function pluginModule() {
  return import('@capacitor/push-notifications');
}

/** Register React Router's navigate so a notification tap can deep-link. */
export function setPushNavigator(nav: (route: string) => void): void {
  navigateRef = nav;
}

async function postToken(token: string): Promise<void> {
  try {
    await request('post', '/devices', { token, platform: clientPlatform() ?? 'android' });
    registeredToken = token;
  } catch (err) {
    // Non-fatal — the bell still works in-app; push just won't reach this
    // device until the next successful registration.
    console.warn('Failed to register push token', err);
  }
}

function handleTap(data: Record<string, unknown> | undefined): void {
  const route = typeof data?.route === 'string' ? data.route : null;
  // Only follow internal app paths. Reject anything that isn't a single-slash
  // absolute path ("//host" is protocol-relative, "http(s)://" is external) so
  // a crafted FCM payload can never navigate the WebView off-app.
  const isInternal = route !== null && route.startsWith('/') && !route.startsWith('//');
  if (isInternal && navigateRef) navigateRef(route);
}

/**
 * Wire listeners (once), request permission, and register for push. Safe to
 * call on every authenticated session start — `register()` re-emits the token.
 */
export async function initPushNotifications(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { PushNotifications } = await pluginModule();

    if (!listenersWired) {
      listenersWired = true;
      await PushNotifications.addListener('registration', (token: Token) => {
        void postToken(token.value);
      });
      await PushNotifications.addListener('registrationError', (err) => {
        console.warn('Push registration error', err);
      });
      await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action: ActionPerformed) => {
          handleTap(action.notification.data as Record<string, unknown> | undefined);
        },
      );
    }

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    await PushNotifications.register();
  } catch (err) {
    console.warn('Push init failed', err);
  }
}

/** Remove this device's token on logout (best-effort; needs a live session). */
export async function removePushRegistration(): Promise<void> {
  if (!isNativePlatform()) return;
  const token = registeredToken;
  registeredToken = null;
  if (!token) return;
  try {
    await request('delete', `/devices/${encodeURIComponent(token)}`);
  } catch {
    // Best-effort — the server prunes dead tokens on the next failed send.
  }
}
